import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { ITrackerEvent } from '../models/ITrackerEvent';
import { IGlimpseUser } from '../../auth/models/IGlimpseUser';
import { resolvePrimaryRole } from '../../auth/models/GlimpseRole';

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  constructor() {
    if (!this.isEnabled()) return;
    try {
      mixpanel.init(environment.mixpanelToken);
    } catch (ex) {}
  }

  private isEnabled = () => !!environment.mixpanelToken;

  identify(id: any) {
    if (!this.isEnabled()) return;
    try {
      // console.log('[Mixpanel] identity:', id);
      mixpanel.identify(id);
    } catch (ex) {}
  }

  track<T>(data: ITrackerEvent<T>) {
    if (!this.isEnabled()) return;

    try {
      // console.log('[Mixpanel] track:', data.event, data.props);
      mixpanel.track(data.event, data.props);
    } catch (ex) {}
  }

  /**
   * Updates the Mixpanel profile and registers Role as a super property, so every subsequent
   * event carries it and funnels can be broken down by role without going through profiles.
   */
  setUserData(user: Partial<IGlimpseUser>) {
    if (!this.isEnabled()) return;

    try {
      const role = this.resolveRole(user.roles);

      const props: any = {
        $name: `${user.firstName} ${user.lastName}`,
        $email: user.email,
        Username: user.username,
        Role: role,
        // Transitional: kept so existing Mixpanel dashboards filtering on Roles keep matching.
        // Drop once they have moved over to Role.
        Roles: user.roles,
        Customers: user.customers ? user.customers.map(c => c.name) : null,
      };

      for (const prop in props) {
        if (!props[prop]) {
          delete props[prop];
        }
      }

      // console.log('[Mixpanel] people.set:', props);
      mixpanel.people.set(props);

      if (role) mixpanel.register({ Role: role });
    } catch (ex) {}
  }

  /** Drops identity and super properties so the next user on this browser starts clean. */
  reset() {
    if (!this.isEnabled()) return;

    try {
      mixpanel.reset();
    } catch (ex) {}
  }

  /**
   * A user is expected to have exactly one role. More than one is a data problem rather than
   * something to represent in analytics: report it to Sentry and carry on with the most
   * privileged one, so tracking degrades instead of breaking.
   */
  private resolveRole(roles: string[] | undefined): string | null {
    if (!roles || !roles.length) return null;

    const role = resolvePrimaryRole(roles);

    if (roles.length > 1) {
      Sentry.captureMessage(
        `User has ${roles.length} roles (${roles.join(', ')}); reporting "${role}" to Mixpanel`,
        'warning',
      );
    }

    return role;
  }
}
